import Vue from 'vue'
import { normalize } from 'normalizr'
import { S, G, M, A } from './types'
import { Getters, Mutations, Actions } from 'vuex'
import { workerSchema } from '@/schema/worker'
import { WorkerEntities } from '@/schema/worker/types'
import { Worker } from '@/types/user'

/* -------------------------------------------------------------------------- */

const state = (): S => ({
  workers: {},
  sort: [],
  totalCount: 0
})

/* -------------------------------------------------------------------------- */

const getters: Getters<S, G> = {
  workers: state => state.workers,
  workersById: state => ids =>
    ids.reduce(
      (wokers, id) =>
        !state.workers[id] ? wokers : wokers.concat(state.workers[id]),
      [] as Worker[]
    ),
  sort: state => (Array.isArray(state.sort) ? state.sort : []),
  totalCount: state => state.totalCount
}

/* -------------------------------------------------------------------------- */

const mutations: Mutations<S, M> = {
  clear: state => {
    state.workers = {}
    state.sort = []
    state.totalCount = 0
  },

  updateList: (state, { workers, sort = [], totalCount = undefined }) => {
    state.totalCount = totalCount
    state.workers = workers
    state.sort = sort
  },

  add: (
    state,
    { workers, totalCount = undefined, sort = undefined, toAppend = true }
  ) => {
    if (sort === undefined) {
      sort = Object.keys(workers).map(id => Number(id))
    }

    sort = sort.filter(id => state.workers[id] === undefined)

    state.sort = toAppend
      ? (state.sort || []).concat(sort)
      : sort.concat(state.sort || [])

    state.workers = Object.assign({}, state.workers, workers)

    state.totalCount = totalCount
  },

  updateWorker: (state, worker) => {
    Vue.set(state.workers, worker.id, worker)
  }
}

/* -------------------------------------------------------------------------- */

const actions: Actions<S, A, G, M> = {
  async updateList({ commit }, { patientIds, page, scope, occupation, query }) {
    const res = await Vue.$api.user.workerList({
      patientIds,
      page,
      scope,
      occupation,
      query
    })
    const { result, entities } = normalize<Worker[], WorkerEntities, number[]>(
      res.results,
      [workerSchema]
    )

    const { workers } = entities

    commit('updateList', {
      totalCount: res.count,
      sort: result,
      workers
    })
  },

  async add(
    { commit },
    { patientIds, scope, occupation, query, page, toAppend = true }
  ) {
    const res = await Vue.$api.user.workerList({
      patientIds,
      scope,
      occupation,
      query,
      page
    })
    const { result, entities } = normalize<Worker[], WorkerEntities, number[]>(
      res.results,
      [workerSchema]
    )

    const { workers } = entities

    commit('add', {
      sort: result,
      workers,
      totalCount: res.count,
      toAppend
    })
  },

  async addOne({ commit }, { workerId, toAppend = true }) {
    const res = await Vue.$api.user.worker({ workerId })

    const { result, entities } = normalize<Worker, WorkerEntities, number>(
      res,
      workerSchema
    )

    const { workers } = entities

    commit('add', {
      sort: [result],
      workers,
      toAppend
    })
  },

  updateWorker: ({ commit }, worker) => {
    commit('updateWorker', worker)
  }
}

/* -------------------------------------------------------------------------- */

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
